import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SNIPPETS, snippetText } from '../data/snippets';
import { CopyIcon } from './Icons';

const COPIED_FOR_MS = 2000;

/** Quick-start snippets per SDK, with the lines to change highlighted. */
export function CodeTabs() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);
  const [copied, setCopied] = useState(false);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const id = useId();

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => {
      setCopied(false);
    }, COPIED_FOR_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);

  const snippet = SNIPPETS[selected];
  if (snippet === undefined) return null;

  const select = (index: number) => {
    setSelected(index);
    setCopied(false);
    tabs.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const last = SNIPPETS.length - 1;
    const next: Record<string, number> = {
      ArrowRight: selected === last ? 0 : selected + 1,
      ArrowLeft: selected === 0 ? last : selected - 1,
      Home: 0,
      End: last,
    };
    const target = next[event.key];
    if (target !== undefined) {
      event.preventDefault();
      select(target);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(snippetText(snippet)).then(
      () => {
        setCopied(true);
      },
      () => {
        setCopied(false);
      },
    );
  };

  return (
    <div className="code-tabs">
      <div
        role="tablist"
        aria-label={t('landing.code.tabs')}
        className="code-tablist"
        onKeyDown={onKeyDown}
      >
        {SNIPPETS.map((item, index) => (
          <button
            key={item.id}
            ref={(element) => {
              tabs.current[index] = element;
            }}
            type="button"
            role="tab"
            id={`${id}-tab-${item.id}`}
            aria-selected={index === selected}
            aria-controls={`${id}-panel`}
            tabIndex={index === selected ? 0 : -1}
            onClick={() => {
              select(index);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div
        className="code-panel"
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-tab-${snippet.id}`}
      >
        <div className="code-bar">
          <span className="code-file">{snippet.file}</span>
          <button type="button" className="code-copy" onClick={copy}>
            <CopyIcon />
            <span aria-live="polite">
              {copied ? t('landing.code.copied') : t('landing.code.copy')}
            </span>
          </button>
        </div>
        <pre tabIndex={0}>
          <code>
            {snippet.lines.map((line, index) => (
              <span key={index} className={line.changed ? 'code-line is-changed' : 'code-line'}>
                {line.text === '' ? ' ' : line.text}
                {'\n'}
              </span>
            ))}
          </code>
        </pre>
      </div>
      <p className="code-legend">
        <span className="code-legend-swatch" aria-hidden="true" />
        {t('landing.code.changed')}
      </p>
    </div>
  );
}
